import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
    pending: "border-slate-500/30 text-slate-400",
    review: "border-amber-400/40 text-amber-200",
    proposed: "border-violet-400/40 text-violet-200",
    residual: "border-slate-700 text-slate-500",
};
const ORDER = { review: 0, proposed: 1, pending: 2, residual: 3 };
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** One unmapped label: status, AI suggestion, assign-to-area select. */
function ReviewRow({ item, areas, disabled, onChanged }) {
    const [area, setArea] = useState(item.suggestion?.suggested_area || (item.suggestion?.area && item.suggestion.area !== "Other" ? item.suggestion.area : ""));
    const [busy, setBusy] = useState(false);
    const id = slug(item.label);
    const assign = async () => {
        if (!area) return;
        setBusy(true);
        try {
            const r = await api.put(`/v1/ripeness/areas/overrides/${encodeURIComponent(item.label)}`, { area, reason: "assigned from QA Radar review list" });
            toast.success(`${item.label} → ${area} (${r.data.tracks_reprojected ?? 0} tracks${r.data.forwarded ? `, on the hub` : ""})`);
            onChanged?.();
        } catch (e) {
            toast.error(`Override failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setBusy(false);
        }
    };
    const sug = item.suggestion;
    return (
        <div className="border border-slate-800 rounded p-2 space-y-1" data-testid={`area-review-row-${id}`}>
            <div className="flex items-center gap-2">
                <span className="text-slate-200 truncate" title={item.label} data-testid={`area-review-label-${id}`}>{item.label}</span>
                <span className="font-mono text-slate-500">{item.count}</span>
                <span className={`ml-auto rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_TONE[item.status] || STATUS_TONE.pending}`} data-testid={`area-review-status-${id}`}>{item.status}</span>
            </div>
            {sug?.rationale ? (
                <div className="text-[11px] text-slate-500 truncate" title={sug.rationale} data-testid={`area-review-suggestion-${id}`}>
                    {sug.proposed_area ? <span className="text-violet-200">proposes “{sug.proposed_area}” · </span> : null}
                    {sug.confidence != null ? <span className="text-slate-400">{Math.round(sug.confidence * 100)}% · </span> : null}{sug.rationale}
                </div>
            ) : null}
            <div className="flex items-center gap-1">
                <select value={area} onChange={(e) => setArea(e.target.value)} disabled={disabled || busy}
                    className="flex-1 min-w-0 rounded border border-slate-700 bg-slate-900 px-1.5 py-1 text-[11px] text-slate-200 disabled:opacity-50"
                    data-testid={`area-review-select-${id}`}>
                    <option value="">Assign area…</option>
                    {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <button onClick={assign} disabled={disabled || busy || !area}
                    className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`area-review-assign-${id}`}>
                    <Check className="w-3 h-3" /> Set
                </button>
            </div>
        </div>
    );
}

/** Unmapped labels (tracks in Other) — review first, residual last; collapsed beyond 8 rows. */
export function AreaReviewList({ items, areas, disabled, onChanged }) {
    const [showAll, setShowAll] = useState(false);
    const [hideResidual, setHideResidual] = useState(true);
    const sorted = [...(items || [])].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || b.count - a.count);
    const visible = hideResidual ? sorted.filter((i) => i.status !== "residual") : sorted;
    const rows = showAll ? visible : visible.slice(0, 8);
    return (
        <div data-testid="area-review-list">
            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Unmapped indications <span className="text-slate-300" data-testid="area-review-count">{visible.length}</span></span>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={hideResidual} onChange={(e) => setHideResidual(e.target.checked)} className="accent-violet-400" data-testid="area-review-hide-residual" />
                    hide residual
                </label>
            </div>
            {rows.length === 0 ? (
                <div className="text-slate-500 italic" data-testid="area-review-empty">Everything is mapped — nothing to review.</div>
            ) : (
                <div className="space-y-1">{rows.map((it) => <ReviewRow key={it.label} item={it} areas={areas} disabled={disabled} onChanged={onChanged} />)}</div>
            )}
            {visible.length > 8 ? (
                <button onClick={() => setShowAll(!showAll)} className="mt-1 text-[11px] text-slate-400 hover:text-slate-200 underline" data-testid="area-review-show-all">
                    {showAll ? "Show fewer" : `Show all ${visible.length}`}
                </button>
            ) : null}
        </div>
    );
}
